import { useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Notice } from "../components/ui/Feedback";
import { Field } from "../components/ui/Input";
import { MicroLabel } from "../components/ui/Text";
import { ApiError } from "../lib/api";
import { useSession } from "../lib/session";

/**
 * Sign in.
 *
 * The layout is the design direction at its most literal: a full-bleed split
 * where the left two-thirds is empty paper carrying one display-size line, and
 * the form occupies a narrow measure on the right against a hairline. It is
 * asymmetric on purpose — a centred card would be the generic option and would
 * say nothing about the product.
 *
 * ## What this component must not do
 *
 * It must not distinguish failure modes. The server answers every bad login with
 * one string ("Invalid email or password") whether the account is unknown,
 * disabled, or simply wrong, and spends real argon2id time either way. Adding a
 * client-side hint — "no account with that address" — would hand back the account
 * enumeration the server pays to prevent.
 */
export function Login() {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const arrival = readArrival(location.state);
  const [email, setEmail] = useState(arrival.email ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  /**
   * Where the operator was headed before being bounced here. Read from router
   * state rather than from a query parameter so it cannot be used to point the
   * post-login redirect at another origin.
   */
  const intended = readIntended(location.state);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;

    setPending(true);
    setError(undefined);

    try {
      await signIn({ email: email.trim(), password });
      navigate(intended, { replace: true });
    } catch (cause) {
      // The server's own message, verbatim — including its 429 text, which is
      // the one case where the operator genuinely needs different advice.
      setError(
        cause instanceof ApiError
          ? cause.message
          : "Sign-in failed. Check your connection and try again.",
      );
      setPassword("");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="grid min-h-dvh grid-cols-1 bg-paper lg:grid-cols-[1.6fr_1fr]">
      <section className="relative hidden flex-col justify-between overflow-hidden border-r border-rule p-section lg:flex">
        <div className="flex flex-col gap-2">
          <span className="font-display text-title font-semibold text-ink">
            OCPP<span className="text-signal">.</span>
          </span>
          <MicroLabel>Central system · 1.6J</MicroLabel>
        </div>

        {/* The one memorable moment. Set at display size, tracked tight, broken
            across three lines so the ragged right edge does the composition. */}
        <h1 className="m-0 max-w-[24ch] font-display text-display font-semibold text-ink">
          Every station,
          <br />
          every card,
          <br />
          <span className="text-signal">every kilowatt-hour.</span>
        </h1>

        <p className="m-0 max-w-prose text-body text-ink-muted">
          Operator access is scoped and every change is recorded — who, what, and the value before
          and after.
        </p>
      </section>

      <section className="flex flex-col justify-center px-gutter py-section lg:px-section">
        <form onSubmit={onSubmit} className="flex w-full max-w-sm flex-col gap-6">
          <div className="flex flex-col gap-2">
            <MicroLabel>Sign in</MicroLabel>
            <h2 className="m-0 font-display text-title font-semibold text-ink">Operator access</h2>
            <div className="mt-1 h-0.5 w-12 bg-rule-strong" />
          </div>

          {arrival.notice === undefined ? null : <Notice tone="ok">{arrival.notice}</Notice>}

          <Field
            label="Email"
            type="email"
            name="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            required
            autoFocus={arrival.email === undefined}
          />

          <Field
            label="Password"
            type="password"
            name="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            autoFocus={arrival.email !== undefined}
            {...(error === undefined ? {} : { error })}
          />

          <Button type="submit" variant="primary" pending={pending} className="self-start">
            {pending ? "Signing in" : "Sign in"}
          </Button>
        </form>
      </section>
    </div>
  );
}

/**
 * Only ever returns an in-app absolute path. A value that arrived from anywhere
 * but this app's own redirect is discarded rather than trusted — `//evil.test` and
 * `https://evil.test` are both valid inputs to `navigate()` and both would leave
 * the origin.
 */
function readIntended(state: unknown): string {
  // `/` rather than a page: the index route picks the landing page from the
  // principal's permissions, which are not known until the sign-in returns.
  const fallback = "/";
  if (typeof state !== "object" || state === null) return fallback;

  const candidate = (state as { from?: unknown }).from;
  if (typeof candidate !== "string") return fallback;
  if (!candidate.startsWith("/") || candidate.startsWith("//")) return fallback;

  return candidate;
}

/**
 * What the set-password page hands over when it sends someone here: the address
 * they just chose a password for, and a line saying it worked. Router state only,
 * for `readIntended`'s reason — it cannot be put there from outside the app, so a
 * crafted link cannot make this page claim a password was set.
 */
function readArrival(state: unknown): { readonly email?: string; readonly notice?: string } {
  if (typeof state !== "object" || state === null) return {};

  const { email, notice } = state as { email?: unknown; notice?: unknown };
  return {
    ...(typeof email === "string" ? { email } : {}),
    ...(typeof notice === "string" ? { notice } : {}),
  };
}
